import { useState, useEffect } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { CalendarBlank, CheckCircle, Clock, ArrowClockwise, CaretDown, CaretRight, ArrowRight, ToggleLeft, ToggleRight } from "@phosphor-icons/react";
import { getCounsellingRounds, formatFees, formatRound, getUnfilledSeatsDetail, getFilterOptions } from "../lib/api";
import ScheduleBanner from "../components/ScheduleBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// URL slug for a counselling body ("Madhya_Pradesh" → "madhya-pradesh").
const slugifyBody = (b) => (b || "").toLowerCase().replace(/[_\s]+/g, "-").replace(/[^a-z0-9-]/g, "");
// Stable, distinct color per state tab (hashed so the same body always maps same).
const STATE_TAB_COLORS = [
  "#6043EF", "#0EA5E9", "#059669", "#DC2626", "#D97706", "#7C3AED",
  "#DB2777", "#0891B2", "#65A30D", "#EA580C", "#4F46E5", "#0D9488",
  "#BE123C", "#9333EA", "#2563EB", "#16A34A", "#C026D3", "#0284C7",
];
const colorForBody = (b) => {
  let h = 0;
  for (let i = 0; i < (b || "").length; i++) h = (h * 31 + b.charCodeAt(i)) >>> 0;
  return STATE_TAB_COLORS[h % STATE_TAB_COLORS.length];
};
const YEAR_OPTIONS = ["2026", "2025", "2024", "2023"];

// Map a seat-matrix pill's `type` field to the numeric `round_number` code the backend
// uses for direct SM viewing. Keep this in sync with server.py's DIRECT SM VIEW branch:
//   full → 50, provisional_rN → 50+N, stray_r1 → 150, stray_special → 250, stray_r101 → 251.
const smTypeToRoundVal = (t) => {
  if (!t) return null;
  if (t === "full") return 50;
  if (t === "stray_r1") return 150;
  if (t === "stray_special") return 250;
  if (t === "stray_r101") return 251;
  const m = /^provisional_r(\d+)$/.exec(t);
  if (m) return 50 + parseInt(m[1], 10);
  return null;
};

export default function CounsellingTracker() {
  const { body: bodyParam, year: yearParam } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [year, setYear] = useState(yearParam || "2025");
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);

  // Unfilled seats state
  const [unfilledBody, setUnfilledBody] = useState("");
  const [unfilledRound, setUnfilledRound] = useState(null);
  const [availableRounds, setAvailableRounds] = useState([]);
  const [roundPills, setRoundPills] = useState([]);
  const [seatMatrixPills, setSeatMatrixPills] = useState([]);
  const [unfilledData, setUnfilledData] = useState(null);
  const [unfilledLoading, setUnfilledLoading] = useState(false);
  const [expandedCollege, setExpandedCollege] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ counselling_bodies: [] });
  const [incrementalMode, setIncrementalMode] = useState(false);

  const bodies = filterOptions.counselling_bodies || [];
  // Navigate to the canonical per-body/year tracker URL.
  const goTo = (b, y) => navigate(`/counselling/tracker/${slugifyBody(b)}/${y}`);

  // Load the counselling-body options once.
  useEffect(() => {
    getFilterOptions().then(r => setFilterOptions(r.data)).catch(() => {});
  }, []);

  // Resolve body + year from the URL path (canonical). On a bare /counselling
  // (no path params) fall back to legacy ?body/?year query, else auto-detect the
  // visitor's state by IP — then redirect to the canonical /tracker/<body>/<year>.
  useEffect(() => {
    if (bodyParam && yearParam) {
      const resolved = bodies.find((b) => slugifyBody(b) === bodyParam) || bodyParam;
      setUnfilledBody(resolved);
      setYear(yearParam);
      localStorage.setItem("counselling_selected_body", resolved);
      return;
    }
    if (!bodies.length) return; // wait for options before detecting/redirecting
    const norm = (s) => (s || "").toLowerCase().replace(/[_\s]+/g, " ").trim();
    const ALIAS = { "all india": "mcc", "medical counselling committee": "mcc" };
    const qBody = sp.get("body"); const qState = sp.get("state"); const qYear = sp.get("year");
    const y = yearParam || qYear || year || "2025";
    // legacy query deep-link (?body=&state=) → resolve then redirect
    let resolved = null;
    for (const cand of [qBody, qState].filter(Boolean)) {
      let c = norm(cand); c = ALIAS[c] || c;
      resolved = bodies.find((x) => norm(x) === c) || bodies.find((x) => norm(x).includes(c) || c.includes(norm(x)));
      if (resolved) break;
    }
    if (resolved) {
      const q = new URLSearchParams();
      if (sp.get("round")) q.set("round", sp.get("round"));
      if (sp.get("sm")) q.set("sm", sp.get("sm"));
      navigate(`/counselling/tracker/${slugifyBody(resolved)}/${y}${q.toString() ? `?${q}` : ""}`, { replace: true });
      return;
    }
    const stored = localStorage.getItem("counselling_selected_body");
    if (stored && bodies.includes(stored)) { goTo(stored, y); return; }
    setDetecting(true);
    fetch("https://ipapi.co/json/")
      .then((r) => r.json())
      .then((d) => {
        const region = (d && d.region) || null;
        let r2 = null;
        if (region) r2 = bodies.find((x) => norm(x) === norm(region)) || bodies.find((x) => norm(x).includes(norm(region)) || norm(region).includes(norm(x)));
        setDetecting(false);
        goTo(r2 || bodies[0] || "Karnataka", y);
      })
      .catch(() => { setDetecting(false); goTo(bodies[0] || "Karnataka", y); });
  }, [bodyParam, yearParam, filterOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the resolved body/year change, (re)load rounds + seat-fill data. Honour
  // any ?round/?sm deep-link on the current URL.
  useEffect(() => {
    if (!unfilledBody || !bodyParam) return;
    setUnfilledData(null); setRoundPills([]); setSeatMatrixPills([]); setUnfilledRound(null);
    loadRounds();
    const rnd = sp.get("round"); const sm = sp.get("sm");
    const smRnd = sm ? smTypeToRoundVal(sm) : null;
    const effRnd = smRnd != null ? smRnd : (rnd ? parseInt(rnd) : undefined);
    loadUnfilledSeats(effRnd, undefined, unfilledBody);
  }, [unfilledBody, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRounds = async () => {
    setLoading(true);
    try {
      const res = await getCounsellingRounds({ year: parseInt(year) });
      setRounds(res.data.rounds || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadUnfilledSeats = async (rnd, useIncremental, bodyOverride) => {
    const body = bodyOverride || unfilledBody;
    setUnfilledLoading(true);
    if (rnd !== undefined) setUnfilledRound(rnd);
    setExpandedCollege(null);
    const targetRound = rnd !== undefined ? rnd : unfilledRound;
    try {
      const params = { counselling_body: body, year: parseInt(year) };
      if (targetRound) params.round_number = targetRound;
      if (useIncremental !== undefined ? useIncremental : incrementalMode) params.mode = "incremental";
      const res = await getUnfilledSeatsDetail(params);
      setUnfilledData(res.data);
      if (res.data.available_rounds) setAvailableRounds(res.data.available_rounds);
      if (res.data.round_pills) setRoundPills(res.data.round_pills);
      // Populate SM pills whenever the backend returns them. Previously this was
      // gated on `!targetRound` which meant deep-links like ?round=201 (or ?sm=stray_r101,
      // now resolved to round 251) never rendered the SEAT MATRIX pill row. Populate on
      // any non-empty response.
      if (res.data.seat_matrix_pills && res.data.seat_matrix_pills.length) setSeatMatrixPills(res.data.seat_matrix_pills);
      if (!targetRound && res.data.round_number) setUnfilledRound(res.data.round_number);
    } catch (e) { console.error(e); }
    setUnfilledLoading(false);
  };

  // Keep only the round/sm deep-link in the query string (body/year now live in
  // the URL path). Lets users bookmark/share the exact round view.
  useEffect(() => {
    if (!bodyParam) return; // don't fight the redirect on bare /counselling
    const next = new URLSearchParams();
    if (unfilledRound != null) {
      const smPill = seatMatrixPills.find((p) => smTypeToRoundVal(p.type) === unfilledRound);
      if (smPill) next.set("sm", smPill.type);
      else next.set("round", String(unfilledRound));
    }
    const cur = sp.toString();
    const nxt = next.toString();
    if (cur !== nxt) setSp(next, { replace: true });
  }, [unfilledRound, seatMatrixPills]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if current round supports incremental toggle
  const currentPill = roundPills.find(p =>
    p.type === "allotment" && p.round === unfilledRound
  );
  const canToggle = currentPill?.has_incremental === true;
  const isIncremental = unfilledData?.mode === "incremental";
  const isStray = unfilledData?.source === "stray";
  const isAllotmentOnly = unfilledData?.no_seat_matrix === true;

  const handleToggle = () => {
    const newMode = !incrementalMode;
    setIncrementalMode(newMode);
    if (unfilledRound) loadUnfilledSeats(unfilledRound, newMode);
  };

  const handleRoundClick = (roundVal) => {
    // For rounds with preceding provisional (R2, R3), default to incremental mode
    const pill = roundPills.find(p => p.type === "allotment" && p.round === roundVal);
    const defaultIncremental = pill?.has_incremental === true;
    setIncrementalMode(defaultIncremental);
    loadUnfilledSeats(roundVal, defaultIncremental);
  };

  // Group rounds by counselling body
  const roundsByBody = {};
  rounds.forEach((r) => {
    if (!roundsByBody[r.counselling_body]) roundsByBody[r.counselling_body] = [];
    roundsByBody[r.counselling_body].push(r);
  });

  const STATUS_STYLES = {
    completed: { bg: "bg-[#00C04B]/10", text: "text-[#00C04B]", border: "border-[#00C04B]", icon: CheckCircle },
    ongoing: { bg: "bg-[#FFC107]/10", text: "text-[#92700C]", border: "border-[#FFC107]", icon: ArrowClockwise },
    awaiting_result: { bg: "bg-[#6043EF]/10", text: "text-[#6043EF]", border: "border-[#6043EF]", icon: Clock },
    upcoming: { bg: "bg-zinc-100", text: "text-[#52525B]", border: "border-zinc-300", icon: Clock },
  };

  return (
    <div className="min-h-screen" data-testid="counselling-tracker-page">
      {/* Header */}
      <div className="border-b border-zinc-200 bg-[#F4F4F5]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 flex items-end justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-1">TRACKING</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-none text-[#09090B] font-heading">
              Counselling Rounds
            </h1>
          </div>
          {detecting && <span className="text-[10px] font-bold uppercase tracking-wider text-[#6043EF] animate-pulse" data-testid="detecting-state">Detecting your state…</span>}
        </div>
      </div>

      {/* Schedule banner — full-width ad-style carousel tied to the selected body */}
      <ScheduleBanner body={unfilledBody} />

      {/* Colored state tabs (per counselling body) + year pills */}
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex gap-1.5 overflow-x-auto py-2.5 -mx-1 px-1 counsel-tabstrip" data-testid="state-tabs">
            {(bodies.length ? bodies : ["Karnataka"]).map((b) => {
              const active = b === unfilledBody;
              const color = colorForBody(b);
              return (
                <button key={b} onClick={() => goTo(b, year)} data-testid={`state-tab-${slugifyBody(b)}`}
                  className="shrink-0 h-8 px-3.5 rounded-full text-[11px] font-bold uppercase tracking-wider border transition-all whitespace-nowrap hover:-translate-y-0.5"
                  style={active
                    ? { background: color, color: "#fff", borderColor: color, boxShadow: `0 6px 16px -6px ${color}` }
                    : { background: "#fff", color, borderColor: `${color}55` }}>
                  {b.replace(/_/g, " ")}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1.5 pb-2.5" data-testid="year-pills">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mr-1">YEAR</span>
            {YEAR_OPTIONS.map((y) => {
              const active = String(y) === String(year);
              return (
                <button key={y} onClick={() => goTo(unfilledBody, y)} data-testid={`year-pill-${y}`}
                  className={`h-7 px-3 rounded-full text-[11px] font-bold font-['IBM_Plex_Mono'] border transition-all ${active ? "bg-[#09090B] text-white border-[#09090B]" : "bg-white text-zinc-500 border-zinc-300 hover:border-[#09090B] hover:text-[#09090B]"}`}>
                  {y}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <Tabs defaultValue="unfilled" className="w-full">
          <TabsList className="w-full justify-start bg-[#F4F4F5] border border-zinc-200 rounded-none h-10 p-0">
            <TabsTrigger value="unfilled" className="rounded-none text-xs font-bold uppercase tracking-[0.15em] data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#6043EF] h-full px-6" data-testid="tab-unfilled" onClick={() => { if (!unfilledData) loadUnfilledSeats(); }}>
              SEAT FILL STATUS
            </TabsTrigger>
            <TabsTrigger value="rounds" className="rounded-none text-xs font-bold uppercase tracking-[0.15em] data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#6043EF] h-full px-6" data-testid="tab-rounds">
              ROUND STATUS
            </TabsTrigger>
          </TabsList>

          {/* ─── SEAT FILL STATUS TAB ──────────────────────────────────── */}
          <TabsContent value="unfilled" className="mt-0">
            <div className="border border-zinc-200 border-t-0">

              {/* Seat Matrix Pills row — clickable */}
              {seatMatrixPills.length > 0 && (
                <div className="p-3 border-b border-zinc-200 flex items-center gap-1 flex-wrap" data-testid="seat-matrix-pills-row">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B] mr-1 w-20">SEAT MATRIX:</span>
                  {seatMatrixPills.map((pill, idx) => {
                    // Compute roundVal via the shared helper so any new SM type (e.g. stray_r101)
                    // is handled consistently with server-side and URL deep-links.
                    const roundVal = smTypeToRoundVal(pill.type);
                    const isActive = unfilledRound === roundVal;
                    const isStray = pill.type.startsWith("stray");
                    return (
                      <span key={pill.key} className="flex items-center gap-1">
                        {idx > 0 && <ArrowRight size={10} weight="bold" className="text-zinc-300" />}
                        <button onClick={() => handleRoundClick(roundVal)}
                          className={`h-7 px-3 text-xs font-['IBM_Plex_Mono'] font-bold transition-colors duration-150 ${
                            isActive
                              ? isStray
                                ? "bg-[#FF2A00] text-white border-2 border-[#FF2A00]"
                                : pill.type === "full"
                                  ? "bg-[#09090B] text-white border-2 border-[#09090B]"
                                  : "bg-[#6043EF] text-white border-2 border-[#6043EF]"
                              : isStray
                                ? "bg-white text-[#FF2A00] hover:bg-[#FF2A00] hover:text-white"
                                : pill.type === "full"
                                  ? "bg-white text-[#09090B] hover:bg-[#09090B] hover:text-white"
                                  : "bg-white text-[#52525B] hover:border-[#6043EF] hover:text-[#6043EF]"
                          }`}
                          style={!isActive ? {
                            backgroundImage: isStray
                              ? `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23FF2A00' stroke-width='2' stroke-dasharray='8%2c 4' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`
                              : pill.type === "full"
                                ? `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2309090B' stroke-width='2' stroke-dasharray='8%2c 4' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`
                                : `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23d4d4d8' stroke-width='2' stroke-dasharray='8%2c 4' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`
                          } : undefined}
                          data-testid={`seat-matrix-pill-${pill.key}`}>
                          {pill.label}
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Precomputed total-available-seats banner — visible whenever the SM
                  view is active (source = provisional_seat_matrix or stray_seat_matrix).
                  Reads `total_available_seats` from the API response; no runtime
                  aggregation on the client. */}
              {unfilledData && (unfilledData.source === "stray_seat_matrix" || unfilledData.source === "provisional_seat_matrix") && unfilledData.total_available_seats != null && (
                <div className="p-3 border-b border-zinc-200 flex items-center gap-3 flex-wrap bg-[#FAFAFA]" data-testid="sm-total-seats-banner">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B]">TOTAL AVAILABLE</span>
                  <span className="inline-flex items-center h-7 px-3 font-['IBM_Plex_Mono'] text-xs font-bold bg-[#09090B] text-white border-2 border-[#09090B]" data-testid="sm-total-seats-value">
                    {unfilledData.total_available_seats} SEATS
                  </span>
                  {unfilledData.sm_aggregate && (
                    <span className="text-[11px] text-zinc-500 font-['IBM_Plex_Mono']">
                      across {unfilledData.sm_aggregate.total_colleges} college{unfilledData.sm_aggregate.total_colleges === 1 ? "" : "s"} · {unfilledData.sm_aggregate.total_rows} row{unfilledData.sm_aggregate.total_rows === 1 ? "" : "s"}
                    </span>
                  )}
                  {unfilledData.sm_aggregate && unfilledData.sm_aggregate.updated_at && (
                    <span className="ml-auto text-[10px] text-zinc-400 font-['IBM_Plex_Mono']">
                      precomputed · {new Date(unfilledData.sm_aggregate.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  )}
                </div>
              )}


              {/* Round Pills — allotment rounds only (no provisionals) + Incremental Toggle */}
              {roundPills.length > 0 && (
                <div className="p-3 border-b border-zinc-200 flex items-center gap-1 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B] mr-1 w-20">ROUND:</span>
                  {roundPills.filter(p => p.type === "allotment").map((pill, idx) => {
                    const roundVal = pill.round;
                    const isActive = unfilledRound === roundVal;
                    const showToggle = isActive && pill.has_incremental;
                    return (
                      <span key={pill.key} className="flex items-center gap-1">
                        {idx > 0 && <ArrowRight size={10} weight="bold" className="text-zinc-300" />}
                        <button onClick={() => handleRoundClick(roundVal)}
                          className={`h-7 px-3 text-xs font-['IBM_Plex_Mono'] font-bold border transition-colors duration-150 ${
                            isActive
                              ? "bg-[#6043EF] text-white border-[#6043EF]"
                              : "bg-white text-[#52525B] border-zinc-300 hover:border-[#6043EF]"
                          }`}
                          data-testid={`unfilled-round-pill-${roundVal}`}>
                          {pill.label}
                        </button>
                        {showToggle && (
                          <button
                            onClick={handleToggle}
                            className={`h-7 px-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] border transition-colors duration-150 ${
                              !incrementalMode
                                ? "bg-[#6043EF] text-white border-[#6043EF]"
                                : "bg-white text-[#52525B] border-zinc-300 hover:border-[#6043EF] hover:text-[#6043EF]"
                            }`}
                            data-testid="incremental-toggle-btn"
                          >
                            {!incrementalMode
                              ? <ToggleRight size={14} weight="bold" />
                              : <ToggleLeft size={14} weight="bold" />
                            }
                            FILLED TILL ROUND
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Summary Bar */}
              {unfilledData && !unfilledLoading && (
                <div className="p-3 border-b border-zinc-200 bg-white flex items-center gap-4 flex-wrap" data-testid="unfilled-summary">
                  <span className="text-xs text-[#52525B]">
                    Viewing: <span className="font-bold text-[#09090B]">{unfilledData.counselling_body} {unfilledData.year}</span>
                    {unfilledData.source === "full_seat_matrix"
                      ? <span className="font-['IBM_Plex_Mono'] font-bold text-[#09090B]"> — FULL SEAT MATRIX (baseline)</span>
                      : <> after <span className="font-['IBM_Plex_Mono'] font-bold text-[#6043EF]">{formatRound(unfilledData.round_number)}</span></>
                    }
                  </span>
                  {unfilledData.source === "full_seat_matrix" && (
                    <Badge className="bg-[#09090B] text-white text-[10px] uppercase tracking-wider font-bold">
                      PUBLISHED BEFORE COUNSELLING — ALL SEATS AVAILABLE
                    </Badge>
                  )}
                  {unfilledData.no_seat_matrix && (
                    <Badge className="bg-[#FFC107] text-[#09090B] text-[10px] uppercase tracking-wider font-bold">
                      NO SEAT MATRIX — Showing allotment counts only
                    </Badge>
                  )}
                  {isStray && (
                    <Badge className="bg-[#52525B] text-white text-[10px] uppercase tracking-wider font-bold">
                      STRAY ROUND — Allotments in this round only
                    </Badge>
                  )}
                  {unfilledData?.source === "stray_seat_matrix" && (
                    <Badge className="bg-[#FF2A00] text-white text-[10px] uppercase tracking-wider font-bold">
                      {unfilledData.message || "STRAY VACANCY — Published vacancies"}
                    </Badge>
                  )}
                  {isIncremental && (
                    <Badge className="bg-[#6043EF] text-white text-[10px] uppercase tracking-wider font-bold">
                      ROUND ONLY — Allotted in R{unfilledData.round_number} vs Available from Prov R{unfilledData.round_number - 1}
                    </Badge>
                  )}
                  <span className="text-xs text-[#52525B]">
                    Colleges: <span className="font-['IBM_Plex_Mono'] font-bold">{unfilledData.total}</span>
                  </span>
                  {!isIncremental && unfilledData.rounds_queried && (
                    <span className="text-[10px] text-zinc-400 font-['IBM_Plex_Mono']">
                      (data from: {unfilledData.rounds_queried.map(r => formatRound(r)).join(" + ")})
                    </span>
                  )}
                </div>
              )}

              {/* Loading */}
              {unfilledLoading && (
                <div className="p-8 text-center">
                  <div className="h-4 w-4 border-2 border-[#6043EF] border-t-transparent animate-spin mx-auto mb-2" />
                  <p className="text-xs text-[#52525B]">Loading seat fill data...</p>
                </div>
              )}

              {/* Results Table */}
              {!unfilledLoading && unfilledData && unfilledData.colleges && (
                <div className="overflow-x-auto" data-testid="unfilled-table">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                        <TableHead className="w-6"></TableHead>
                        <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] min-w-[200px]">COLLEGE</TableHead>
                        <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">TYPE</TableHead>
                        <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">COURSE</TableHead>
                        <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">
                          {isAllotmentOnly ? "ALLOTTED" : isStray ? "FILLED" : isIncremental ? "ALLOTTED" : "FILLED"}
                        </TableHead>
                        {!isAllotmentOnly && (
                        <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">
                          {isStray ? "MATRIX TOTAL" : isIncremental ? "AVAILABLE" : "TOTAL"}
                        </TableHead>
                        )}
                        {!isStray && !isAllotmentOnly && (
                        <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">
                          {isIncremental ? "REMAINING" : "UNFILLED"}
                        </TableHead>
                        )}
                        {!isAllotmentOnly && (
                        <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] w-[140px]">
                          {isStray ? "% OF TOTAL" : isIncremental ? "ALLOT %" : "FILL %"}
                        </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {consolidateQuotaRows(unfilledData.colleges).map((c, i) => (
                        <CollegeUnfilledRow key={`${c.college_code}_${c.course || "MBBS"}_${i}`} c={c} i={i}
                          expanded={expandedCollege === i}
                          onToggle={() => setExpandedCollege(expandedCollege === i ? null : i)}
                          isIncremental={isIncremental}
                          isStray={isStray}
                          isAllotmentOnly={isAllotmentOnly} />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Empty state */}
              {!unfilledLoading && !unfilledData && (
                <div className="p-12 text-center">
                  <CalendarBlank size={40} className="text-zinc-300 mx-auto mb-4" />
                  <p className="text-sm text-[#52525B]">Pick a state and year above to view seat fill status</p>
                </div>
              )}

              {!unfilledLoading && unfilledData && unfilledData.colleges?.length === 0 && (
                <div className="p-12 text-center">
                  <p className="text-sm text-[#52525B]">No data found for the selected counselling body and year.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ─── ROUND STATUS TAB ──────────────────────────────────────── */}
          <TabsContent value="rounds" className="mt-0">
            {loading ? (
              <div className="space-y-2 mt-4">
                {[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-zinc-200 animate-pulse" />)}
              </div>
            ) : (
              <div className="space-y-0 mt-0" data-testid="rounds-list">
                {Object.entries(roundsByBody).map(([body, bodyRounds]) => (
                  <div key={body} className="border border-zinc-200 border-t-0">
                    <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200">
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">{body}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-7 gap-0">
                      {bodyRounds.sort((a, b) => a.round_number - b.round_number).map((r) => {
                        const style = STATUS_STYLES[r.status] || STATUS_STYLES.upcoming;
                        const Icon = style.icon;
                        return (
                          <div key={r.id} className="p-4 border-r border-b border-zinc-200" data-testid={`round-${body}-${r.round_number}`}>
                            <div className="flex items-center gap-1.5 mb-2">
                              <Icon size={14} weight="bold" className={style.text} />
                              <span className="font-['IBM_Plex_Mono'] text-xs font-bold">{formatRound(r.round_number)}</span>
                            </div>
                            <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${style.bg} ${style.text} ${style.border}`}>
                              {r.status === "awaiting_result" ? "AWAITING" : r.status}
                            </Badge>
                            {r.publication_date && (
                              <p className="font-['IBM_Plex_Mono'] text-[10px] text-[#6043EF] mt-2">{r.publication_date}</p>
                            )}
                            {r.start_date && (
                              <p className="font-['IBM_Plex_Mono'] text-[10px] text-zinc-400 mt-1">{r.start_date}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {Object.keys(roundsByBody).length === 0 && (
                  <div className="border border-zinc-200 border-t-0 p-8 text-center">
                    <CalendarBlank size={40} className="text-zinc-300 mx-auto mb-4" />
                    <p className="text-sm text-[#52525B]">No counselling round data available for {year}</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/**
 * Consolidate quota-encoded rows (e.g. RJ_XXX__GOVT + RJ_XXX__MGMT) into one row per physical college+course.
 * Non-quota rows pass through unchanged.
 */
function consolidateQuotaRows(colleges) {
  const grouped = {};
  const order = [];

  for (const c of colleges) {
    const code = c.college_code || "";
    const sep = code.indexOf("__");
    const baseCode = sep > 0 ? code.substring(0, sep) : code;
    const quota = sep > 0 ? code.substring(sep + 2) : "";
    const course = c.course || "MBBS";
    const key = `${baseCode}_${course}`;

    if (!quota) {
      // No quota encoding — pass through as-is
      if (!grouped[key]) { grouped[key] = null; order.push(key); }
      // Store directly (null = passthrough)
      grouped[key] = c;
      continue;
    }

    if (!grouped[key]) {
      order.push(key);
      grouped[key] = {
        college_code: baseCode,
        college_name: c.college_name,
        college_type: c.college_type,
        course,
        total_filled: 0,
        total_seats: 0,
        total_unfilled: 0,
        fill_percentage: 0,
        categories: [],
        quotas: [],
      };
    }
    // If grouped[key] was a passthrough (no quotas array), convert it to grouped format
    const g = grouped[key];
    if (!g.quotas) {
      const prev = { ...g };
      g.total_filled = prev.total_filled || 0;
      g.total_seats = prev.total_seats || 0;
      g.total_unfilled = prev.total_unfilled || 0;
      g.fill_percentage = 0;
      g.categories = prev.categories ? [...prev.categories] : [];
      g.quotas = [];
      g.college_code = baseCode;
    }
    g.total_filled += (c.total_filled || 0);
    g.total_seats += (c.total_seats || 0);
    g.total_unfilled += (c.total_unfilled || 0);
    g.quotas.push({
      quota,
      total_filled: c.total_filled || 0,
      total_seats: c.total_seats || 0,
      total_unfilled: c.total_unfilled || 0,
      fill_percentage: c.fill_percentage || 0,
      categories: c.categories || [],
    });
    // Merge categories from all quotas
    for (const cat of (c.categories || [])) {
      g.categories.push({ ...cat, quota });
    }
    // Recalculate fill percentage
    g.fill_percentage = g.total_seats > 0 ? Math.round(g.total_filled / g.total_seats * 1000) / 10 : 0;
  }

  return order.map(k => grouped[k]);
}

function CollegeUnfilledRow({ c, i, expanded, onToggle, isIncremental, isStray, isAllotmentOnly }) {
  const fillColor = c.fill_percentage >= 95 ? "bg-[#00C04B]" : c.fill_percentage >= 80 ? "bg-[#FFC107]" : "bg-[#FF2A00]";
  const textColor = c.fill_percentage >= 95 ? "text-[#00C04B]" : c.fill_percentage >= 80 ? "text-[#92700C]" : "text-[#FF2A00]";
  const colSpan = isAllotmentOnly ? 5 : isStray ? 7 : 8;
  const hasQuotas = c.quotas && c.quotas.length > 0;

  // Clean display code (strip __QUOTA if present)
  const codeStr = c.college_code || "";
  const quotaSep = codeStr.indexOf("__");
  const displayCode = quotaSep > 0 ? codeStr.substring(0, quotaSep) : codeStr;

  return (
    <>
      <TableRow className="hover:bg-zinc-50 cursor-pointer transition-colors duration-150" onClick={onToggle} data-testid={`unfilled-row-${i}`}>
        <TableCell className="w-6 px-2">
          <CaretRight size={10} weight="bold" className={`text-zinc-400 transition-transform duration-150 ${expanded ? "rotate-90" : ""}`} />
        </TableCell>
        <TableCell className="font-medium text-sm">
          {c.college_name?.split(',')[0]}
          <span className="block text-[10px] text-zinc-400 font-['IBM_Plex_Mono']">{displayCode}</span>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{c.college_type}</Badge>
          {hasQuotas && (
            <span className="block text-[10px] text-zinc-400 mt-0.5 font-['IBM_Plex_Mono']">{c.quotas.length} quotas</span>
          )}
        </TableCell>
        <TableCell className="font-['IBM_Plex_Mono'] text-xs font-bold">{c.course || "MBBS"}</TableCell>
        <TableCell className="text-right font-['IBM_Plex_Mono'] font-semibold text-sm">{c.total_filled}</TableCell>
        {!isAllotmentOnly && (
        <TableCell className="text-right font-['IBM_Plex_Mono'] text-sm text-[#52525B]">{c.total_seats}</TableCell>
        )}
        {!isStray && !isAllotmentOnly && (
        <TableCell className={`text-right font-['IBM_Plex_Mono'] font-bold text-sm ${c.total_unfilled > 0 ? "text-[#FF2A00]" : "text-[#00C04B]"}`}>
          {c.total_unfilled}
        </TableCell>
        )}
        {!isAllotmentOnly && (
        <TableCell>
          <div className="flex items-center gap-2">
            {!isStray && (
            <div className="flex-1 h-2 bg-zinc-200">
              <div className={`h-full ${fillColor} transition-all duration-300`} style={{ width: `${Math.min(100, c.fill_percentage)}%` }} />
            </div>
            )}
            <span className={`font-['IBM_Plex_Mono'] text-xs font-bold ${isStray ? "text-[#52525B]" : textColor} ${isStray ? "" : "w-12"} text-right`}>
              {c.fill_percentage > 0 ? `${c.fill_percentage}%` : "—"}
            </span>
          </div>
        </TableCell>
        )}
      </TableRow>
      {/* Expanded: quota breakdown + category breakdown */}
      {expanded && (
        <TableRow className="bg-zinc-50 hover:bg-zinc-50">
          <TableCell colSpan={colSpan} className="p-0">
            <div className="px-8 py-3 animate-fade-in">
              {isIncremental && (
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#6043EF] mb-2">
                  SHOWING: ALLOTTED IN THIS ROUND / AVAILABLE FROM PROVISIONAL
                </p>
              )}
              {isStray && (
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#52525B] mb-2">
                  SHOWING: FILLED IN THIS STRAY ROUND / MATRIX TOTAL
                </p>
              )}
              {isAllotmentOnly && (
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#52525B] mb-2">
                  SHOWING: ALLOTTED COUNTS PER CATEGORY (NO SEAT MATRIX AVAILABLE)
                </p>
              )}

              {/* Quota-level breakdown (Rajasthan-style) */}
              {hasQuotas && (
                <div className="mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#09090B] mb-1.5">QUOTA BREAKDOWN</p>
                  <div className="flex flex-wrap gap-2">
                    {c.quotas.map((q, qi) => {
                      const qFill = q.total_seats > 0 ? Math.round(q.total_filled / q.total_seats * 100) : 0;
                      const qColor = qFill >= 80 ? "border-[#00C04B]/40 bg-[#00C04B]/5" : qFill >= 50 ? "border-[#FFC107]/40 bg-[#FFC107]/5" : "border-[#FF2A00]/40 bg-[#FF2A00]/5";
                      return (
                        <div key={qi} className={`border px-3 py-1.5 ${qColor}`}>
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${
                            q.quota === "GOVT" ? "text-[#00C04B]" :
                            q.quota === "NRI" ? "text-[#7C3AED]" :
                            q.quota === "MGMT" ? "text-[#FF2A00]" :
                            q.quota === "GEN" ? "text-[#6043EF]" : "text-[#09090B]"
                          }`}>{q.quota}</span>
                          <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B] ml-2">
                            {q.total_filled}/{q.total_seats}
                          </span>
                          <span className="font-['IBM_Plex_Mono'] text-[10px] text-zinc-400 ml-1">({qFill}%)</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Category-level breakdown */}
              {c.categories && c.categories.length > 0 && (
                <>
                  {hasQuotas && <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#09090B] mb-1.5">CATEGORY DETAIL (ALL QUOTAS)</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {c.categories
                      .filter(cat => cat.total > 0 || cat.filled > 0)
                      .sort((a, b) => (a.quota || "").localeCompare(b.quota || "") || a.category.localeCompare(b.category, undefined, { numeric: true }))
                      .map((cat, ci) => {
                      const isOverflow = !isAllotmentOnly && cat.filled > cat.total;
                      const label = cat.quota ? `${cat.quota}:${cat.category}` : cat.category;
                      return (
                      <div key={ci} className={`border px-2 py-1 text-[10px] font-['IBM_Plex_Mono'] ${
                        isAllotmentOnly ? "border-zinc-300 bg-zinc-50" :
                        isStray ? "border-zinc-300 bg-zinc-50" :
                        isOverflow ? "border-[#FF2A00] bg-[#FF2A00]/10" :
                        cat.unfilled > 0 ? "border-[#FF2A00]/30 bg-[#FF2A00]/5" : "border-[#00C04B]/30 bg-[#00C04B]/5"
                      }`}>
                        <span className={`font-bold ${isOverflow && !isStray ? "text-[#FF2A00]" : ""}`}>{label}</span>
                        <span className={`ml-1 ${isOverflow && !isStray ? "text-[#FF2A00] font-bold" : "text-[#52525B]"}`}>
                          {isAllotmentOnly ? cat.filled : isStray ? cat.filled : `${cat.filled}/${cat.total}`}
                        </span>
                        {cat.unfilled > 0 && !isOverflow && !isStray && !isAllotmentOnly && <span className="text-[#00C04B] ml-1">({cat.unfilled})</span>}
                      </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
